// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@werner/App/Components/Notify/Notify";
import LazyImage, { LazyImageProps } from "@werner/MobiusOverrides/LazyImage/LazyImage";
import ProductPrice, { ProductPriceStyles } from "@werner/Overrides/Components/ProductPrice";
import Trash from "@werner/XDL/Icons/Trash";
import { color, ellipsis, hideInputNumberArrows } from "@werner/XDL/Utilities";
import { xScreen } from "@werner/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    hideTippy: () => void;
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    leftSideWrapper?: InjectableCss;
    rightSideWrapper?: InjectableCss;
    lineDescription?: LinkPresentationProps;
    productInfoWrapper?: InjectableCss;
    qtyAndPriceWrapper?: InjectableCss;
    qtyAndRemoveWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    extendedPriceWrapper?: InjectableCss;
    basicProductPriceStyles?: ProductPriceStyles;
    extendedNetPriceValue?: TypographyProps;
    totalPriceWrapper?: InjectableCss;
    cartLineTotal?: TypographyProps;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    seperator?: InjectableCss;
}

const miniCartLineStyles: MiniCartLineStyles = {
    cartLine: {
        css: css`
            display: flex;
            position: relative;
            padding: 12px 0;
            margin-left: 30px;
            align-items: center;
            justify-content: flex-start;
            &:hover {
                cursor: pointer;
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 60px;
            height: 60px;
            overflow: hidden;
            margin-right: 15px;
            flex-shrink: 0;
            ${xScreen("lte", "sm")} {
                margin-right: 4px;
            }
        `,
    },
    cartLineImage: {
        css: css`
            max-height: 100%;
            max-width: 100%;
            img {
                height: auto;
            }
        `,
    },
    detailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${xScreen("lte", "sm")} {
                width: 100%;
                max-width: initial;
            }
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            gap: 22px;
            width: 100%;
        `,
    },
    rightSideWrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            margin-right: 5px;
        `,
    },
    productInfoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
        `,
    },
    lineDescription: {
        typographyProps: {
            size: 15,
            lineHeight: "22px",
            weight: 400,
            css: css`
                letter-spacing: 0;
                text-decoration: none;
                ${ellipsis(2)}
                &&&& {
                    color: ${color("common.ui3")};
                    &:hover {
                        text-decoration: underline;
                    }
                }
            `,
        },
    },
    qtyAndPriceWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-end;
            width: 100%;
        `,
    },
    qtyAndRemoveWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            max-width: 64px;
            margin-right: 32px;
        `,
    },
    cartLineQuantityStyles: {
        editableQuantityTextField: {
            sizeVariant: "small",
            labelProps: {
                css: css`
                    display: none;
                `,
            },
            cssOverrides: {
                formField: css`
                    width: auto;
                `,
                inputSelect: css`
                    width: 64px;
                    height: 46px;
                    text-align: center;
                    font-size: 15px;
                    font-weight: 400;
                    letter-spacing: 0;
                    line-height: 18px;
                    ${hideInputNumberArrows};
                `,
            },
        },
    },
    extendedPriceWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            margin-right: 20px;
            width: 80px;
            & > section > div {
                display: flex;
                flex-wrap: wrap;
            }
        `,
    },
    totalPriceWrapper: {
        css: css`
            margin-right: 10px;
            max-width: 80px;
            min-width: 65px;
        `,
    },
    removeCartLineLink: {
        css: css`
            margin-left: 18px;
            margin-right: auto;
            ${IconWrapper} {
                &:hover {
                    color: ${color("primary")};
                }
            }
        `,
    },
    cartLineTotal: {
        size: 15,
        lineHeight: "18px",
        weight: 500,
        css: css`
            white-space: nowrap;
        `,
    },
    errorMessagesWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            & > div:not(:last-child) {
                margin-bottom: 15px;
            }
        `,
    },
    leftSideWrapper: {
        css: css`
            max-width: 293px;
            width: 100%;
        `,
    },
    seperator: {
        css: css`
            margin: 0 20px;
            border-bottom: 1px solid ${color("common.border")};
            &:last-child {
                border-bottom: none;
            }
        `,
    },
};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard = ({
    cartLine,
    errorMessages,
    hideTippy,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
}: Props) => {
    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                <StyledWrapper {...styles.imageWrapper}>
                    <Link href={cartLine.productUri} onClick={hideTippy} aria-label={`${cartLine.altText} link`}>
                        <LazyImage
                            src={cartLine.smallImagePath}
                            altText={cartLine.altText}
                            {...styles.cartLineImage}
                        />
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.detailsWrapper}>
                    <StyledWrapper {...styles.contentWrapper}>
                        <StyledWrapper {...styles.leftSideWrapper} className="miniCart-leftSide">
                            <Link
                                href={cartLine.productUri}
                                onClick={hideTippy}
                                {...styles.lineDescription}
                                aria-label={`${cartLine.shortDescription} Link`}
                            >
                                {cartLine.shortDescription}
                            </Link>
                        </StyledWrapper>
                        <StyledWrapper className="miniCart-rightSide" {...styles.rightSideWrapper}>
                            <StyledWrapper {...styles.qtyAndRemoveWrapper}>
                                <CartLineQuantity
                                    cart={cart as Cart}
                                    editable={true}
                                    extendedStyles={styles.cartLineQuantityStyles}
                                    onQtyOrderedChange={(inputQty: number) => {
                                        qtyOrderedChangeHandler && qtyOrderedChangeHandler(cartLine, inputQty);
                                    }}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.extendedPriceWrapper}>
                                <ProductPrice
                                    showLabel={false}
                                    product={cartLine}
                                    extendedStyles={styles.basicProductPriceStyles}
                                    failedToLoadRealtimePricing={cart?.cartNotPriced}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.totalPriceWrapper}>
                                {!cart?.cartNotPriced &&
                                cartLine &&
                                cartLine.pricing &&
                                cartLine.pricing.extendedUnitNetPrice > 0 ? (
                                    <Typography {...styles.cartLineTotal}>
                                        {cartLine.pricing.extendedUnitNetPriceDisplay}
                                    </Typography>
                                ) : null}
                            </StyledWrapper>
                            <StyledWrapper>
                                <Link
                                    onClick={() => removeCartLineClickHandler(cartLine)}
                                    {...styles.removeCartLineLink}
                                    aria-label="Remove Cart Line"
                                >
                                    <Icon src={Trash} size={18} />
                                </Link>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>

                    {errorMessages && (
                        <StyledWrapper {...styles.errorMessagesWrapper}>
                            {errorMessages.map(message => (
                                <Notify
                                    type="danger"
                                    data-test-selector="cartline_errorMessage"
                                    key={`error_${cartLine.id}`}
                                    showClose={false}
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.seperator}></StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
